import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { getAllConfettiIcons } from '../../../utils/industryConfettiConfig';

export default function ConfettiSelector({ selectedIcons = [], onSave, onClose }) {
  const [selected, setSelected] = useState(selectedIcons);
  const allIcons = getAllConfettiIcons();

  const toggleIcon = (iconData) => {
    const key = `${iconData?.label}-${iconData?.color}`;
    const exists = selected?.some(item => `${item?.label}-${item?.color}` === key);
    
    if (exists) {
      setSelected(selected?.filter(item => `${item?.label}-${item?.color}` !== key));
    } else {
      setSelected([...selected, iconData]);
    }
  };

  const isSelected = (iconData) => {
    const key = `${iconData?.label}-${iconData?.color}`;
    return selected?.some(item => `${item?.label}-${item?.color}` === key);
  };

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Select Confetti Icons
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose multiple icons to mix and match for this industry
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Selected Count */}
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
          <p className="text-sm text-blue-700">
            <span className="font-semibold">{selected?.length}</span> icon{selected?.length !== 1 ? 's' : ''} selected
          </p>
        </div>

        {/* Icon Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
            {allIcons?.map((iconData, index) => {
              const IconComponent = iconData?.icon;
              const selected = isSelected(iconData);
              
              return (
                <button
                  key={`${iconData?.label}-${iconData?.color}-${index}`}
                  onClick={() => toggleIcon(iconData)}
                  className={`relative flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                    selected
                      ? 'border-blue-500 bg-blue-50' :'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  title={iconData?.label}
                >
                  {selected && (
                    <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                  )}
                  <IconComponent
                    className="w-6 h-6 mb-1"
                    style={{ color: iconData?.color }}
                    strokeWidth={2}
                  />
                  <span className="text-xs text-gray-600 text-center truncate w-full">
                    {iconData?.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            type="button"
            variant="outline"
            onClick={() => setSelected([])}
            disabled={selected?.length === 0}
          >
            Clear All
          </Button>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={selected?.length === 0}
            >
              Save Selection ({selected?.length})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
